// src/components/Antarctica.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Link, useLocation } from "react-router-dom";
import "./photographs.css";
import AudioFloat from "./AudioFloat.js";

/* -------------------------------------
   Body data attribute (light nav mode)
------------------------------------- */
function useLightNav(flag = true) {
  useEffect(() => {
    const el = document?.body;
    if (!el) return;
    if (flag) el.setAttribute("data-light-nav", "true");
    return () => el.removeAttribute("data-light-nav");
  }, [flag]);
}

/* -------------------------------------
   Detect mobile vs desktop
------------------------------------- */
function useIsMobile(query = "(max-width: 991px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);

    mql.addEventListener
      ? mql.addEventListener("change", on)
      : mql.addListener(on);

    return () => {
      mql.removeEventListener
        ? mql.removeEventListener("change", on)
        : mql.removeListener(on);
    };
  }, [query]);

  return isMobile;
}

/* -------------------------------------
   Reveal on scroll
------------------------------------- */
function useRevealOnScroll(deps = []) {
  useEffect(() => {
    const nodes = document.querySelectorAll(".photographs-page .reveal");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "10% 0px -4% 0px" }
    );

    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
}

/* -------------------------------------
   Helpers
------------------------------------- */
function getOrder(p) {
  const raw =
    (p?.order && (p.order.$numberInt ?? p.order.$numberLong)) ??
    p?.order ??
    9_999_999;

  if (typeof raw === "number") return raw;

  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 9_999_999;
  }

  return 9_999_999;
}

/* -------------------------------------
   Component
------------------------------------- */
export default function Antarctica({ fetchPhotographs, photographs }) {
  useLightNav(true);
  const isMobile = useIsMobile();
  const location = useLocation();

  /* ---------- which sub-collection are we in? ---------- */
  const { filterKey, filterValue, pageTitle, themeClass, emptyText } = useMemo(
    () => {
      const path = (location.pathname || "").replace(/\/+$/, "");
      const slug = path.split("/").filter(Boolean).slice(-1)[0] || "";

      // themes:
      // frosty-theme   = bright ice / glacial veil
      // vessel-theme   = warm hull / amber
      // fading-theme   = twilight violet / ember
      const map = {
        "white-continent": {
          key: "antarctica",
          value: true,
          title: "THE WHITE CONTINENT",
          theme: "frosty-theme",
        },
        "unspoken-journeys": {
          key: "journey",
          value: true,
          title: "UNSPOKEN JOURNEYS",
          theme: "frosty-theme",
        },
        "silent-vessels": {
          key: "vessel",
          value: true,
          title: "SILENT VESSELS",
          theme: "vessel-theme",
        },
        fading: {
          key: "fading",
          value: true,
          title: "THE FADING LIGHT",
          theme: "fading-theme",
        },
      };

      if (map[slug]) {
        const { key, value, title, theme } = map[slug];
        return {
          filterKey: key,
          filterValue: value,
          pageTitle: title,
          themeClass: theme,
          emptyText: "",
        };
      }

      return {
        filterKey: null,
        filterValue: null,
        pageTitle: "PHOTOGRAPHS",
        themeClass: "frosty-theme",
        emptyText: "",
      };
    },
    [location.pathname]
  );

  /* ---------- filter + sort ---------- */
  const list = useMemo(() => {
    if (!photographs) return [];
    const arr = Array.isArray(photographs)
      ? photographs
      : Object.values(photographs);

    const filtered = arr.filter((p) => {
      if (!p) return false;
      if (!p.imageURL) return false;
      if (!filterKey) return true;
      return p[filterKey] === filterValue;
    });

    return filtered.sort((a, b) => getOrder(a) - getOrder(b));
  }, [photographs, filterKey, filterValue]);

  /* ---------- VIEW PREFS ---------- */
  // desktop columns are user-toggleable
  const [desktopCols, setDesktopCols] = useState(() => {
    try {
      return Number(localStorage.getItem("lvl_photos_desktop_cols")) || 3;
    } catch {
      return 3;
    }
  });

  // mobile: locked to single column for this section
  const mobileCols = 1;

  /* ---------- framing / mat prefs ---------- */
  const [framed, setFramed] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_frame") === "1";
    } catch {
      return false;
    }
  });

  const [matSize, setMatSize] = useState(() => {
    try {
      return localStorage.getItem("lvl_grid_mat") || "standard";
    } catch {
      return "standard";
    }
  }); // "standard" | "large"

  // track load count just in case we want it later; no gating now
  const [loadedCount, setLoadedCount] = useState(0);

  /* ---------- persist prefs ---------- */
  useEffect(() => {
    try {
      localStorage.setItem("lvl_photos_desktop_cols", String(desktopCols));
    } catch {}
  }, [desktopCols]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_frame", framed ? "1" : "0");
    } catch {}
  }, [framed]);

  useEffect(() => {
    try {
      localStorage.setItem("lvl_grid_mat", matSize);
    } catch {}
  }, [matSize]);

  /* ---------- fetch data on mount ---------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof fetchPhotographs === "function") {
      fetchPhotographs();
    }
  }, [fetchPhotographs]);

  /* ---------- scroll reveal ---------- */
  useRevealOnScroll([
    list,
    desktopCols,
    framed,
    matSize,
    isMobile,
  ]);

  /* ---------- img load handler ---------- */
  const handleImgLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      img.classList.add("is-loaded");

      const article = img.closest(".ph-card");
      const slot = img.closest(".card-link");

      if (article) {
        const { naturalWidth: w, naturalHeight: h } = img;
        const isLandscape = w >= h;

        if (slot) {
          slot.classList.toggle("is-landscape", isLandscape);
          slot.classList.toggle("is-portrait", !isLandscape);
          slot.setAttribute(
            "data-shape",
            isLandscape ? "landscape" : "portrait"
          );
        }

        article.classList.toggle("is-landscape", isLandscape);
        article.classList.toggle("is-portrait", !isLandscape);
        article.setAttribute(
          "data-shape",
          isLandscape ? "landscape" : "portrait"
        );
      }

      setLoadedCount((c) => c + 1);
    },
    []
  );

  /* ---------- classes ---------- */
  const gridClasses = [
    "photographs-grid",
    isMobile
      ? "m-one" // Antarctica view stays single col on mobile
      : desktopCols === 2
      ? "d-two"
      : "d-three",
    framed ? "framed-on" : "framed-off",
    framed ? (matSize === "large" ? "mat-large" : "mat-standard") : "",
  ]
    .filter(Boolean)
    .join(" ");

  // match the stabilized pattern:
  // always treat page as ready so images show, no blocking intro
  const rootClass = `photographs-page page-is-ready ${themeClass}`;

  /* ---------- per-series subcopy ---------- */
  const subtextBySeries = {
    "The White Continent":
      "A study of silence, scale, and survival at Earth’s edge. These works were created in brutal cold and open water, then finished as limited edition fine art prints.",
    "Unspoken Journeys":
      "Some places reshape you quietly — the movement itself becoming memory.",
    "Silent Vessels":
      "Fishermen drift through dusk — their boats like prayers of light.",
    "The Fading Light":
      "Canoes crossing gold water — silhouettes dissolving into memory.",
    PHOTOGRAPHS:
      "An ongoing record of human presence in fragile environments. Each work is photographed in the field and finished in limited edition fine art print.",
  };

  const subCopy =
    subtextBySeries[pageTitle] ||
    "An ongoing record of human presence in fragile environments. Each work is photographed in the field and finished in limited edition fine art print.";

  /* ---------- render ---------- */
  return (
    <div className={rootClass}>
      <div className="main-page page-pad smaller black">
        {/* Head */}
        <header className="page-head reveal">
          <h1 className="page-title-centered">{pageTitle}</h1>
          <p className="centered-sub">{subCopy}</p>
        </header>

        {/* Controls */}
        <div
          className="photographs-controls reveal"
          role="group"
          aria-label="View and frame controls"
        >
          {/* Desktop column control only (hide on mobile) */}
          {!isMobile && (
            <div
              className="view"
              role="group"
              aria-label="Desktop view density"
            >
              <span className="view-label">View</span>
              <button
                type="button"
                onClick={() => setDesktopCols(2)}
                className={
                  desktopCols === 2 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 2}
              >
                Duo
              </button>
              <button
                type="button"
                onClick={() => setDesktopCols(3)}
                className={
                  desktopCols === 3 ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={desktopCols === 3}
              >
                Trio
              </button>
            </div>
          )}

          {/* Frame toggle */}
          <label className="frame-toggle" aria-label="Framing preview toggle">
            <input
              type="checkbox"
              checked={framed}
              onChange={(e) => {
                const next = e.target.checked;
                setFramed(next);
              }}
            />
            <span>Framing Preview</span>
          </label>

          {/* Matting toggle, only if framed */}
          {framed && (
            <div className="mat-toggle" aria-label="Matting size options">
              <span className="view-label">Matting</span>
              <button
                type="button"
                onClick={() => setMatSize("standard")}
                className={
                  matSize === "standard" ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={matSize === "standard"}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => setMatSize("large")}
                className={
                  matSize === "large" ? "view-spn selected-btn" : "view-spn"
                }
                aria-pressed={matSize === "large"}
              >
                Gallery
              </button>
            </div>
          )}
        </div>

        {/* GRID */}
        <div className={gridClasses}>
          {list.length === 0 ? (
            <div
              className="smaller uppercase meta"
              style={{
                gridColumn: "1 / -1",
                textAlign: "center",
                opacity: 0.7,
              }}
            >
              {emptyText}
            </div>
          ) : (
            list.map((p) => {
              const { _id, imageURL, title, series, location, year } = p || {};
              const key = _id || imageURL;
              if (!imageURL) return null;

              return (
                <Link
                  to={`/photographs/${_id}`}
                  key={key}
                  className="card-link reveal"
                  data-shape=""
                >
                  <article
                    className={[
                      "ph-card",
                      framed ? "has-frame" : "",
                    ].join(" ")}
                  >
                    <div className="ph-media">
                      <div className="ph-media-inner">
                        <img
                          className="ph-img"
                          src={imageURL}
                          alt={title || "Photograph"}
                          loading="lazy"
                          decoding="async"
                          onLoad={handleImgLoad}
                          onError={handleImgLoad}
                        />
                      </div>
                    </div>

                    <div className="ph-meta">
                      {series ? (
                        <div className="ph-series text-gray">{series}</div>
                      ) : null}
                      {title ? (
                        <div className="ph-title-case">{title}</div>
                      ) : null}

                      <div className="ph-lines">
                        {location ? (
                          <div className="ph-location text-gray-2">
                            {location}
                          </div>
                        ) : null}
                        {year ? (
                          <div className="ph-year italic">{year}</div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                </Link>
              );
            })
          )}
        </div>
      </div>

      {/* Audio player */}
      <AudioFloat
        srcs={[
          {
            src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/shop.mp3",
            type: "audio/mpeg",
          },
        ]}
        startMuted={false}
        startVolume={0.5}
        loop={true}
        label="Site background audio"
        startTime={190}
      />
    </div>
  );
}
